import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  createSharepointApi,
  updateSharepointApi,
  deleteSharepointApi,
} from '../api/sharepointApi'
import LogTool from '../../../logger/logTools'
import { sharepointKeys } from '../utils/queryKeys'

const log = new LogTool({ context: 'SharepointMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to create a sharepoint
 */
export function useCreateSharepoint(): UseMutationResult<any, Error, any> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createSharepointApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      log.info('Sharepoint created successfully')
    },
    onError: (error) => {
      log.error('Error creating sharepoint:', error)
    },
  })
}

/**
 * Hook to update a sharepoint
 */
export function useUpdateSharepoint(): UseMutationResult<
  any,
  Error,
  { sharepointId: string | number; data: any }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sharepointId, data }) => updateSharepointApi(sharepointId, data),
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      await queryClient.invalidateQueries({ 
        queryKey: sharepointKeys.detail(variables.sharepointId)
      })
      log.info('Sharepoint updated successfully')
    },
    onError: (error) => {
      log.error('Error updating sharepoint:', error)
    },
  })
}

/**
 * Hook to delete a sharepoint
 */
export function useDeleteSharepoint(): UseMutationResult<void, Error, string | number> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteSharepointApi,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sharepointKeys.lists() })
      log.info('Sharepoint deleted successfully')
    },
    onError: (error) => {
      log.error('Error deleting sharepoint:', error)
    },
  })
}

